import { ESymbolRoles } from '@Core/Reels/SymbolRoles';
import { IAuthenticateGameConfig } from '@GFTypes/index';

//TODO: make this based on config
enum EDoubleCoinSymbolIds {
    BLANK = 0,
    COIN = 9,
    MINI = 10,
    MINOR = 11,
    MAJOR = 12,
    GRAND = 13
}

export const doubleCoinIdsArray = [
    EDoubleCoinSymbolIds.BLANK,
    EDoubleCoinSymbolIds.COIN,
    EDoubleCoinSymbolIds.MINI,
    EDoubleCoinSymbolIds.MINOR,
    EDoubleCoinSymbolIds.MAJOR,
    EDoubleCoinSymbolIds.GRAND
] as const;

export const isDoubleLockAndRollCoinPresent = (gameConfig: IAuthenticateGameConfig): boolean => {
    return !!getSymbolIdWithDoubleCoinRole(gameConfig);
};

export const getSymbolIdWithDoubleCoinRole = (gameConfig: IAuthenticateGameConfig): number | undefined => {
    return gameConfig.symbols
        .flat()
        .sort((symbolA, symbolB) => symbolA.symbolId - symbolB.symbolId)
        .find((symbol) => symbol.roles?.some((role) => role.types.includes(ESymbolRoles.LOCK_AND_ROLL_DOUBLE_COIN)))?.symbolId;
};

export const decodeDoubleCoinSymbolId = (id: number, startId: number = 9): [number, number] => {
    const offset = id - startId - 1;

    if (!Number.isInteger(offset) || offset < 0 || offset >= 36) {
        throw new RangeError(`Invalid combination ID: ${id}`);
    }

    // Left: Blank–Minor, Right: Blank–Minor
    if (offset < 16) {
        return [Math.floor(offset / 4), offset % 4];
    }

    // Left: Blank–Minor, Right: Major–Grand
    if (offset < 24) {
        const localOffset = offset - 16;
        return [Math.floor(localOffset / 2), 4 + (localOffset % 2)];
    }

    // Left: Major–Grand, Right: Blank–Grand
    const localOffset = offset - 24;
    return [4 + Math.floor(localOffset / 6), localOffset % 6];
};

export const encodeDoubleCoinSymbolId = ([left, right]: readonly [number, number], startId: number = 9): number => {
    if (![left, right].every((symbol) => Number.isInteger(symbol) && symbol >= 0 && symbol <= 5)) {
        throw new RangeError(`Invalid symbol pair: [${left}, ${right}]`);
    }

    let offset: number;

    if (left >= 4) {
        offset = 24 + (left - 4) * 6 + right;
    } else if (right >= 4) {
        offset = 16 + left * 2 + (right - 4);
    } else {
        offset = left * 4 + right;
    }

    return startId + 1 + offset;
};

export const createDoubleCoinMap = (startId: number = 9): Map<number, [number, number]> => {
    if (doubleCoinIdsArray.length !== 6) {
        throw new RangeError('Expected exactly 6 replacement symbol IDs');
    }

    return new Map(
        Array.from({ length: 36 }, (_, index): [number, [number, number]] => {
            const id = startId + 1 + index;
            const [left, right] = decodeDoubleCoinSymbolId(id, startId);

            return [id, [doubleCoinIdsArray[left], doubleCoinIdsArray[right]]];
        })
    );
};
